import { useState, useRef, useEffect } from "react";
import { Upload, message } from "antd";
import { Video, UploadIcon, XIcon } from "lucide-react";
import axios from "axios";
import { UrlImage } from "../lib/api";

const { Dragger } = Upload;

export default function FileUploadVideo({
  onUploadComplete,
  name,
  video,
  disabled,
}) {
  const [uploadState, setUploadState] = useState({
    file: null,
    preview: video || null,
    uploading: false,
    progress: 10,
    remainingTime: null,
    uploadedUrl: null,
    error: null,
  });

  useEffect(() => {
    if (video) {
      setUploadState({ ...uploadState, preview: video });
    } else {
      setUploadState({ ...uploadState, preview: null });
    }
  }, [video]);

  const uploadStartTime = useRef(null);
  const uploadSpeeds = useRef([]);

  useEffect(() => {
    return () => {
      if (uploadState.preview) {
        URL.revokeObjectURL(uploadState.preview);
      }
    };
  }, [uploadState.preview]);

  const calculateRemainingTime = (loaded, total) => {
    if (!uploadStartTime.current) {
      uploadStartTime.current = Date.now();
      return null;
    }

    const elapsedTime = (Date.now() - uploadStartTime.current) / 1000;
    const uploadSpeed = loaded / elapsedTime;

    uploadSpeeds.current.push(uploadSpeed);
    if (uploadSpeeds.current.length > 5) {
      uploadSpeeds.current.shift();
    }

    const avgSpeed =
      uploadSpeeds.current.reduce((sum, speed) => sum + speed, 0) /
      uploadSpeeds.current.length;
    const remainingBytes = total - loaded;
    const remainingTime = remainingBytes / avgSpeed;

    return Math.round(remainingTime);
  };

  const formatRemainingTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds} seconds remaining`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds
        .toString()
        .padStart(2, "0")} minutes remaining`;
    }
  };

  const handleUpload = async (file) => {
    uploadStartTime.current = null;
    uploadSpeeds.current = [];

    setUploadState((prev) => ({
      ...prev,
      file,
      preview: URL.createObjectURL(file),
      uploading: true,
      progress: 0,
      remainingTime: null,
      error: null,
    }));

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);

    try {
      const response = await axios.post(
        "https://pro1.edunezam.com/api/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              const remainingTime = calculateRemainingTime(
                progressEvent.loaded,
                progressEvent.total
              );

              setUploadState((prev) => ({
                ...prev,
                progress: percentCompleted,
                remainingTime,
              }));
            }
          },
        }
      );

      if (response.data && response.data.url) {
        setUploadState((prev) => ({
          ...prev,
          uploading: false,
          progress: 100,
          uploadedUrl: response.data.url,
        }));

        if (onUploadComplete) {
          onUploadComplete(response.data.url);
        }

        message.success("Video uploaded successfully");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      setUploadState((prev) => ({
        ...prev,
        uploading: false,
        error: "Upload failed. Please try again.",
      }));
      message.error("Upload failed. Please try again.");
    }
  };

  const removeVideo = () => {
    if (uploadState.preview) {
      URL.revokeObjectURL(uploadState.preview);
    }
    onUploadComplete(null);
    setUploadState({
      file: null,
      preview: null,
      uploading: false,
      progress: 0,
      remainingTime: null,
      uploadedUrl: null,
      error: null,
    });
  };

  const customRequest = ({ file }) => {
    if (file instanceof File) {
      handleUpload(file);
    }
    return { abort: () => {} };
  };

  const beforeUpload = (file) => {
    const isVideo = file.type.startsWith("video/");
    if (!isVideo) {
      message.error("You can only upload video files!");
      return false;
    }

    const isLt100M = file.size / 1024 / 1024 < 100;
    if (!isLt100M) {
      message.error("Video must be smaller than 100MB!");
      return false;
    }

    return true;
  };

  return (
    <div className="space-y-4 h-full">
      {!uploadState.preview ? (
        <Dragger
          showUploadList={false}
          beforeUpload={beforeUpload}
          customRequest={customRequest}
          accept="video/*"
          disabled={disabled}
          className="rounded-lg transition-colors duration-300 ease-in-out h-full"
        >
          <div className="flex flex-col items-center justify-center text-center">
            <UploadIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <p className="text-sm text-muted-foreground">
              Drag and drop the video here, or click to select.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Supported formats: MP4, WebM, MOV
            </p>
          </div>
        </Dragger>
      ) : (
        <div className="relative group rounded-xl overflow-hidden">
          <video
            src={video ? `${UrlImage}${video}` : uploadState.preview}
            autoPlay
            loop
            muted
            className="w-full h-52 object-contain rounded-lg"
          />

          {uploadState.uploading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
              <div className="w-full h-full relative overflow-hidden e-card playing">
                <div className="wave wave1"></div>
                <div className="wave wave2"></div>
                <div className="wave wave3"></div>

                {/* Percentage Display */}
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="text-white text-4xl font-bold drop-shadow-lg">
                    {uploadState.progress}%
                  </div>
                </div>

                {/* Remaining Time */}
                {uploadState.remainingTime !== null && (
                  <div className="absolute bottom-4 left-0 right-0 text-center z-10">
                    <p className="text-white bg-black/30 px-3 py-1 rounded-full inline-block text-sm">
                      {formatRemainingTime(uploadState.remainingTime)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Video className="h-12 w-12 text-white" />
              </div>
              {!disabled && (
                <button
                  onClick={removeVideo}
                  className="absolute top-2 right-2 bg-white/80 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                >
                  <XIcon className="h-5 w-5 text-gray-700" />
                </button>
              )}
            </>
          )}
        </div>
      )}
      {uploadState.error && (
        <div className="text-red-500 text-sm mt-2">{uploadState.error}</div>
      )}
    </div>
  );
}
